import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { officeDashboardStats } from "@/lib/office.functions";
import { Inbox, FileText, Package, Calendar, ArrowRight, TrendingUp } from "lucide-react";

export const Route = createFileRoute("/_employee/medewerker/office/")({
  head: () => ({ meta: [{ title: "Dashboard — EVG Office" }, { name: "robots", content: "noindex" }] }),
  component: OfficeDashboard,
});

function KPI({ label, value, to, icon: Icon, tint }: any) {
  return (
    <Link
      to={to}
      className="group relative overflow-hidden rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200 hover:shadow-md transition"
    >
      <div className={`absolute right-4 top-4 flex size-10 items-center justify-center rounded-xl ${tint}`}>
        <Icon className="size-5" />
      </div>
      <div className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-2 text-3xl font-bold text-slate-900">{value}</div>
      <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#0b2a4a] group-hover:gap-2 transition-all">
        Bekijken <ArrowRight className="size-3" />
      </div>
    </Link>
  );
}

function OfficeDashboard() {
  const fn = useServerFn(officeDashboardStats);
  const { data } = useQuery({ queryKey: ["office-stats"], queryFn: () => fn() });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Overzicht</h1>
        <p className="text-sm text-slate-500">Welkom terug — hier vind je alle openstaande zaken.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPI label="Nieuwe aanvragen" value={data?.newRequests ?? "…"} to="/medewerker/office/aanvragen" icon={Inbox} tint="bg-amber-100 text-amber-700" />
        <KPI label="Open offertes" value={data?.openQuotes ?? "…"} to="/medewerker/office/offertes" icon={FileText} tint="bg-sky-100 text-sky-700" />
        <KPI label="Lopende orders" value={data?.openOrders ?? "…"} to="/medewerker/office/orders" icon={Package} tint="bg-emerald-100 text-emerald-700" />
        <KPI label="Vandaag ingepland" value={data?.planningToday ?? "…"} to="/medewerker/office/planning" icon={Calendar} tint="bg-violet-100 text-violet-700" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <div className="flex items-center gap-2">
            <TrendingUp className="size-4 text-[#0b2a4a]" />
            <h2 className="font-semibold">Snelle acties</h2>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            <Link to="/medewerker/office/offertes/nieuw" className="rounded-lg border border-slate-200 p-4 hover:border-[#0b2a4a] hover:bg-slate-50 transition">
              <div className="text-sm font-semibold text-slate-900">+ Nieuwe offerte</div>
              <div className="mt-1 text-xs text-slate-500">Stel handmatig een offerte op voor een klant.</div>
            </Link>
            <Link to="/medewerker/office/aanvragen" className="rounded-lg border border-slate-200 p-4 hover:border-[#0b2a4a] hover:bg-slate-50 transition">
              <div className="text-sm font-semibold text-slate-900">Bekijk aanvragen</div>
              <div className="mt-1 text-xs text-slate-500">Volg nieuwe leads op vanuit de offertepijplijn.</div>
            </Link>
            <Link to="/medewerker/office/planning" className="rounded-lg border border-slate-200 p-4 hover:border-[#0b2a4a] hover:bg-slate-50 transition">
              <div className="text-sm font-semibold text-slate-900">Planning</div>
              <div className="mt-1 text-xs text-slate-500">Installaties inplannen of verplaatsen.</div>
            </Link>
            <Link to="/medewerker/office/klanten" className="rounded-lg border border-slate-200 p-4 hover:border-[#0b2a4a] hover:bg-slate-50 transition">
              <div className="text-sm font-semibold text-slate-900">Klanten</div>
              <div className="mt-1 text-xs text-slate-500">Zoek en bekijk klantgegevens.</div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
